'use client';

import { zodResolver } from '@hookform/resolvers/zod';
import Link from 'next/link';
import { useSearchParams } from 'next/navigation';
import { Suspense } from 'react';
import { useForm } from 'react-hook-form';

import { ROUTES } from '@/constants/routes';

import { useLoginMutation } from '@/modules/auth/hooks/use-login-mutation';
import { loginSchema, type LoginFormValues } from '@/modules/auth/schemas/auth-forms.schema';

import { Alert } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';

function LoginFormFallback() {
  return (
    <Card>
      <CardHeader>
        <Skeleton className="h-6 w-40" />
        <Skeleton className="h-4 w-full" />
      </CardHeader>
      <div className="space-y-4">
        <Skeleton className="h-11 w-full" rounded="md" />
        <Skeleton className="h-11 w-full" rounded="md" />
        <Skeleton className="h-11 w-full" rounded="md" />
      </div>
    </Card>
  );
}

function LoginForm() {
  const searchParams = useSearchParams();
  const nextParam = searchParams.get('next');
  const loginMutation = useLoginMutation(nextParam);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormValues>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: '',
      password: '',
    },
  });

  const onSubmit = handleSubmit((values) => {
    loginMutation.mutate(values);
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle>Sign in</CardTitle>
        <CardDescription>
          Enter the email and password issued to your VunaPay workspace.
        </CardDescription>
      </CardHeader>

      <form className="flex flex-col gap-4" onSubmit={onSubmit} noValidate>
        <Input
          autoComplete="email"
          label="Work email"
          type="email"
          error={errors.email?.message}
          {...register('email')}
        />
        <Input
          autoComplete="current-password"
          label="Password"
          type="password"
          error={errors.password?.message}
          {...register('password')}
        />

        <div className="flex items-center justify-between text-sm">
          <Link href={ROUTES.forgotPassword} className="font-medium text-primary underline-offset-4 hover:underline">
            Forgot password?
          </Link>
        </div>

        <Button type="submit" className="w-full" loading={loginMutation.isPending}>
          Continue
        </Button>

        <p className="text-center text-sm text-muted-foreground">
          New to VunaPay?{' '}
          <Link className="font-semibold text-primary underline-offset-4 hover:underline" href={ROUTES.register}>
            Create an account
          </Link>
        </p>

        <Alert variant="info" title="Security notice">
          <p>
            Sessions enforce short-lived access tokens with rotating refresh credentials. Avoid sharing
            credentials and always sign out on shared devices.
          </p>
        </Alert>
      </form>
    </Card>
  );
}

export default function LoginPage() {
  return (
    <Suspense fallback={<LoginFormFallback />}>
      <LoginForm />
    </Suspense>
  );
}
